import {
  BaseModal,
  BaseModalBody,
  BaseModalHeader,
  Button,
  Label,
  Select,
} from "@src/common/components";
import { useCourierLocale } from "@src/common/courierLocale/useCourierLocale";
import { FormEvent, useEffect, useState } from "react";
import { useAssignableBillableItems } from "../hooks/useAssignableBillableItems";
import { useAssignBillableItemToOrder } from "../hooks/useAssignBillableItemToOrder";
import { useOrderBillableItem } from "../hooks/useOrderBillableItem";
import {
  billableItemAssignmentOptionLabel,
  buildBillableItemSelectOptions,
} from "../utils/billableItemAssignmentOptions";

export interface AssignBillableItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  shipmentId?: string;
  shipmentTemplateId?: string;
}

const UNAVAILABLE_BILLABLE_ITEM_SUFFIX = " (no longer available)";
/**
 * Only items actively linked to the order's end customer are offered, because the backend
 * rejects any other assignment.
 *
 * The placeholder stays short: the Select grows to fit its placeholder and would push past
 * the modal. The reason lives in wrapping helper text below the control instead.
 */
const NO_LINKED_BILLABLE_ITEMS_PLACEHOLDER = "No billable items for this customer";
const NO_LINKED_BILLABLE_ITEMS_HINT =
  "Link a billable item to this customer under Configuration → Billable Items.";
/**
 * Shown only while an item is selected. An assigned order is absorbed into the billable
 * item's invoice line and skipped when the invoice is rendered, so the dispatcher should
 * know the charge moves before confirming. Clearing the selection reverses it, which is
 * why this must not appear on the unassign path.
 */
const ASSIGNMENT_INVOICE_WARNING =
  "Once assigned, this order is billed as part of the billable item. It will no longer appear as its own line on the invoice.";

/**
 * Assigns (or clears) the billable item an order is billed under.
 *
 * Works for both live orders and recurring templates: the caller passes whichever id
 * its page has, and the modal loads the order, its end customer, and the current
 * assignment itself.
 */
export function AssignBillableItemModal({
  isOpen,
  onClose,
  shipmentId,
  shipmentTemplateId,
}: AssignBillableItemModalProps) {
  const { formatCurrency } = useCourierLocale();
  const {
    orderId,
    endCustomerId,
    billableItem,
    loading: isLoadingOrder,
    refetch,
  } = useOrderBillableItem({ shipmentId, shipmentTemplateId });
  const { assignableBillableItems, isLoadingBillableItems } =
    useAssignableBillableItems(endCustomerId, isOpen);
  const { assignBillableItemToOrder, isAssigning } =
    useAssignBillableItemToOrder();
  const [selectedBillableItemId, setSelectedBillableItemId] = useState<
    string | null
  >(null);

  useEffect(() => {
    setSelectedBillableItemId(billableItem?.id ?? null);
  }, [billableItem?.id, isOpen, orderId]);

  const assignableOptions = buildBillableItemSelectOptions(
    assignableBillableItems,
    formatCurrency
  );
  const isCurrentBillableItemUnavailable =
    !!billableItem &&
    !assignableOptions.some((option) => option.id === billableItem.id);
  /**
   * A since-deleted or since-unlinked assignment is absent from `assignableOptions`
   * (the backend would reject re-submitting it), but the select must still show it as
   * selected so the dispatcher can clear it — otherwise unassigning is impossible.
   */
  const options =
    billableItem && isCurrentBillableItemUnavailable
      ? [
          ...assignableOptions,
          {
            id: billableItem.id,
            label: `${billableItemAssignmentOptionLabel(
              billableItem,
              formatCurrency
            )}${UNAVAILABLE_BILLABLE_ITEM_SUFFIX}`,
          },
        ]
      : assignableOptions;
  const selectedOptions = options.filter(
    (option) => option.id === selectedBillableItemId
  );
  const isLoadingSelectData = isLoadingBillableItems || isLoadingOrder;
  const hasNoBillableItems = !isLoadingSelectData && options.length === 0;

  const handleBillableItemChange = (params: any) => {
    const value: { id: string; label: string } | undefined = params.value[0];
    setSelectedBillableItemId(value?.id ?? null);
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!orderId) return;
    assignBillableItemToOrder(orderId, selectedBillableItemId)
      .then(() => {
        refetch();
        onClose();
      })
      .catch(() => undefined);
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose}>
      <BaseModalHeader>Assign Billable Item</BaseModalHeader>
      <BaseModalBody>
        <form onSubmit={onSubmit} className="grid gap-2">
          <div className="flex-1 flex flex-col gap-2">
            <Label>Billable Item</Label>
            <Select
              clearable={true}
              searchable={true}
              isLoading={isLoadingSelectData}
              disabled={hasNoBillableItems}
              options={options}
              value={selectedOptions}
              placeholder={
                hasNoBillableItems
                  ? NO_LINKED_BILLABLE_ITEMS_PLACEHOLDER
                  : "Select a billable item"
              }
              onChange={handleBillableItemChange}
            />
            {hasNoBillableItems && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {NO_LINKED_BILLABLE_ITEMS_HINT}
              </p>
            )}
            {selectedBillableItemId && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                {ASSIGNMENT_INVOICE_WARNING}
              </p>
            )}
          </div>
          <div className="flex flex-row justify-end mt-3">
            <Button isProcessing={isAssigning} type="submit">
              Confirm
            </Button>
          </div>
        </form>
      </BaseModalBody>
    </BaseModal>
  );
}
